import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Clock, Server } from "lucide-react";
import { useEffect, useState } from "react";
import type { DeploymentStatus } from "@/app/servers/types";
import { getRelativeTimeString } from "@/lib/dates";

// Constants for timing
const HEALTH_UPDATE_INTERVAL = 1000; // 1 second
const TOOLTIP_DELAY_DURATION = 200; // milliseconds

type ServerHealthIndicatorProps = {
  health?: { healthy: boolean; message?: string };
  error: Error | null;
  dataUpdatedAt: number;
  deploymentStatus?: DeploymentStatus;
  isManaged?: boolean;
};

// Helper function to determine status color
const getStatusColor = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): string => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "running") {
      return "text-green-500";
    }
    if (deploymentStatus === "failed") {
      return "text-red-500";
    }
    if (deploymentStatus === "unknown") {
      return "text-slate-500";
    }
    if (deploymentStatus === "pending" || deploymentStatus === "updating") {
      return "text-amber-500";
    }
  }

  if (error) {
    return "text-amber-500";
  }
  if (!health) {
    return "text-slate-400";
  }
  return health.healthy ? "text-green-500" : "text-red-500";
};

// Helper function to determine status text
const getStatusText = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): string => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "unknown") {
      return "Unknown";
    }
    if (deploymentStatus === "pending") {
      return "Deploying";
    }
    if (deploymentStatus === "updating") {
      return "Updating";
    }
    if (deploymentStatus === "running") {
      return "Running";
    }
    if (deploymentStatus === "failed") {
      return "Failed";
    }
  }

  if (error) {
    return "Error";
  }
  if (!health) {
    return "Checking...";
  }
  return health.healthy ? "Healthy" : "Unhealthy";
};

// Helper function to determine background color
const getBackgroundColor = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): string => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "running") {
      return "bg-green-500/15";
    }
    if (deploymentStatus === "failed") {
      return "bg-red-500/15";
    }
    if (deploymentStatus === "unknown") {
      return "bg-slate-500/20 animate-pulse";
    }
    if (deploymentStatus === "pending" || deploymentStatus === "updating") {
      return "bg-amber-500/15 animate-pulse";
    }
  }

  if (error) {
    return "bg-slate-500/30";
  }
  if (!health) {
    return "animate-pulse";
  }
  return health.healthy ? "bg-green-500/15" : "bg-red-500/15";
};

// Helper function to determine dot color
const getDotColor = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): string => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "running") {
      return "bg-green-500";
    }
    if (deploymentStatus === "failed") {
      return "bg-red-500";
    }
    if (deploymentStatus === "unknown") {
      return "bg-slate-500";
    }
    if (deploymentStatus === "pending" || deploymentStatus === "updating") {
      return "bg-amber-500";
    }
  }

  if (error) {
    return "bg-slate-500";
  }
  if (!health) {
    return "bg-slate-500";
  }
  return health.healthy ? "bg-green-500" : "bg-red-500";
};

export const ServerHealthIndicator = ({
  health,
  error,
  dataUpdatedAt,
  deploymentStatus,
  isManaged,
}: ServerHealthIndicatorProps) => {
  const [timeString, setTimeString] = useState<string | null>(null);

  // Status color, text, background, and dot color for the server icon
  const statusColor = getStatusColor(
    error,
    health,
    deploymentStatus,
    isManaged
  );
  const status = getStatusText(error, health, deploymentStatus, isManaged);
  const backgroundColor = getBackgroundColor(
    error,
    health,
    deploymentStatus,
    isManaged
  );
  const dotColor = getDotColor(error, health, deploymentStatus, isManaged);

  // Update time string
  useEffect(() => {
    if (!dataUpdatedAt) {
      return;
    }

    const updateTime = () => {
      try {
        setTimeString(getRelativeTimeString(dataUpdatedAt));
      } catch {
        setTimeString(new Date(dataUpdatedAt).toLocaleTimeString());
      }
    };

    updateTime();
    const interval = setInterval(updateTime, HEALTH_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  return (
    <TooltipProvider delay={TOOLTIP_DELAY_DURATION}>
      <Tooltip>
        <TooltipTrigger
          render={<div className="flex cursor-pointer flex-col items-center" />}
        >
          <div className={cn("relative rounded-full p-2", backgroundColor)}>
            <Server
              aria-label={`Server status: ${status}`}
              className={cn("h-5 w-5", statusColor)}
            />
            <div
              className={cn(
                "absolute top-0 right-0 h-2 w-2 rounded-full",
                dotColor
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-xs">
            <p className="font-medium">Status: {status}</p>

            {(error || (health && !health.healthy && health.message)) && (
              <div className="mt-2">
                <pre className="max-h-32 overflow-auto rounded border border-red-200 bg-red-50 p-2 text-red-800 text-xs dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300">
                  <code>
                    {health?.message || error?.message || "Unknown error"}
                  </code>
                </pre>
              </div>
            )}

            {!!timeString && (
              <div className="mt-2 flex items-center gap-1 text-muted-foreground text-xs">
                <Clock className="h-3 w-3" />
                <span>Last checked: {timeString}</span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
